import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import useAuth from 'contexts/auth/useAuth';
import { useCompanyFeatureFlags } from 'hooks/use-company-feature-flags';
import { useGetKnowledgeHubStepsStatusesQuery } from 'store/api/knowledge-hub/knowledge-hub.api';

import { KNOWLEDGE_HUB_STEPS } from '../constants';

import type {
  KnowledgeHubSteps,
  KnowledgeHubStepsStatuses,
} from 'api/endpoints/knowledge-hub/knowledge-hub.types';

export type UseKnowledgeHubSteps = {
  progress: number;
  currentStep: KnowledgeHubSteps[number] | undefined;
  steps: KnowledgeHubSteps;
  variableStep: KnowledgeHubSteps[number] | undefined;
  productStep: KnowledgeHubSteps[number] | undefined;
  positioningStep: KnowledgeHubSteps[number] | undefined;
  competitorsStep: KnowledgeHubSteps[number] | undefined;
  isDone: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isFatalError: boolean;
};

function parseSteps(steps: KnowledgeHubSteps) {
  return steps.filter((item) => item.name !== 'done');
}

function getSteps(
  steps: KnowledgeHubStepsStatuses,
  { isKnowledgeHubCompetitorsEnabled = false } = {},
) {
  let blockNext = false;

  return KNOWLEDGE_HUB_STEPS.reduce((acc, step) => {
    if (step.name === 'competitors' && !isKnowledgeHubCompetitorsEnabled) {
      return acc;
    }

    const status =
      step.name === 'done' ? 'done' : steps[step.name]?.status || 'pending';
    const isBlocked = blockNext && status !== 'done';

    if (status !== 'done') {
      blockNext = true;
    }

    return [
      ...acc,
      {
        ...step,
        status,
        blocked: isBlocked,
      },
    ];
  }, [] as KnowledgeHubSteps);
}

function getProgress(steps: KnowledgeHubSteps) {
  if (!steps.length) {
    return 0;
  }

  const parsedSteps = parseSteps(steps);
  const doneSteps = parsedSteps.filter((item) => item.status === 'done').length;

  return Math.floor((doneSteps / parsedSteps.length) * 100);
}

function getCurrentStep(
  steps: KnowledgeHubSteps,
  stepByCurrentURL: KnowledgeHubSteps[number] | undefined,
) {
  const parsedSteps = parseSteps(steps);
  const isError = steps.some((item) => item.status === 'error');
  const isNotStarted = steps.some((item) => item.status === 'not-started');
  const isPending = steps.some((item) => item.status === 'pending');
  const isGenerating = steps.some((item) => item.status === 'generating');
  const isDone = parsedSteps.every((item) => item.status === 'done');

  if (isError) {
    return steps.find((item) => item.status === 'error');
  }

  if (stepByCurrentURL?.status === 'done') {
    return stepByCurrentURL;
  }

  if (isGenerating) {
    return steps.find((item) => item.status === 'generating');
  }

  if (isPending || isNotStarted) {
    const pendingStep = steps.find((item) => item.status === 'pending');
    const notStartedStep = steps.find((item) => item.status === 'not-started');

    if (pendingStep && notStartedStep) {
      return steps.indexOf(notStartedStep) < steps.indexOf(pendingStep)
        ? notStartedStep
        : pendingStep;
    }

    if (isPending) {
      return pendingStep;
    }

    return notStartedStep;
  }

  if (isDone) {
    return parsedSteps[parsedSteps.length - 1];
  }

  return steps[0];
}

export function useKnowledgeHubSteps(): UseKnowledgeHubSteps {
  const { company } = useAuth();
  const { isKnowledgeHubCompetitorsEnabled } = useCompanyFeatureFlags(
    'isKnowledgeHubCompetitorsEnabled',
  );
  const organizationId = company?.ownOrganizationId;
  const location = useLocation();

  const {
    data: stepsStatuses,
    isLoading: isLoadingStepsStatuses,
    isFetching: isFetchingStepsStatuses,
    isError: isStepsStatusesError,
  } = useGetKnowledgeHubStepsStatusesQuery(
    {
      organizationId: organizationId as number,
    },
    {
      skip: !organizationId,
      refetchOnMountOrArgChange: true,
    },
  );

  const stepsStatusesUnknown =
    !stepsStatuses || isLoadingStepsStatuses || isStepsStatusesError;
  const steps = useMemo(() => {
    if (stepsStatusesUnknown) {
      return [];
    }

    return getSteps(stepsStatuses.steps, {
      isKnowledgeHubCompetitorsEnabled,
    });
  }, [
    stepsStatusesUnknown,
    stepsStatuses?.steps,
    isKnowledgeHubCompetitorsEnabled,
  ]);

  const stepByCurrentURL = steps?.find(
    (step) => step.url === location.pathname,
  );

  const progress = useMemo(() => getProgress(steps) || 0, [steps]);
  const currentStep = useMemo(
    () => getCurrentStep(steps, stepByCurrentURL),
    [steps, stepByCurrentURL],
  );
  const productStep = steps.find((step) => step.name === 'products');
  const positioningStep = steps.find((step) => step.name === 'positioning');
  const variableStep = steps.find((step) => step.name === 'variables');
  const competitorsStep = steps.find((step) => step.name === 'competitors');

  const isFatalError = steps.some((step) => step.status === 'error');

  const isDone =
    !!steps.length && steps.every((step) => step.status === 'done');

  return {
    progress,
    currentStep,
    steps,
    variableStep,
    productStep,
    positioningStep,
    competitorsStep,
    isDone,
    isLoading: isLoadingStepsStatuses,
    isFetching: isFetchingStepsStatuses,
    isError: isStepsStatusesError,
    isFatalError,
  };
}
